import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Briefcase, CalendarClock, ClipboardList, Copy, FileText, MapPin, Plus, Users } from "lucide-react";
import { getContractorWorkspace, inviteContractorMember } from "@/lib/contractors.functions";
import {
  PARTNERSHIP_STATUS_LABEL, contractedShiftStatus, partnershipOperational, shiftHours,
  type PartnershipStatus,
} from "@/lib/contractors";
import { ShiftStatusBadge } from "@/components/contractors/ShiftStatusBadge";
import { ContractorWorkflowStrip } from "@/components/contractors/ContractorWorkflowStrip";
import { ContractorDisclaimer } from "@/components/contractors/ContractorDisclaimer";

export const Route = createFileRoute("/_authenticated/contractor/")({
  component: ContractorHome,
  head: () => ({
    meta: [
      { title: "Contracted Work — MissionReady" },
      { name: "description", content: "Your contracted assignment: assigned sites, post orders, contracted shifts and pay periods." },
      { property: "og:title", content: "Contracted Work — MissionReady" },
      { property: "og:description", content: "Assigned sites, contracted shifts and timesheets for your contractor organization." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

export function useContractorWorkspace() {
  const { user } = useAuth();
  const fn = useServerFn(getContractorWorkspace);
  return useQuery({
    queryKey: ["contractor-workspace", user?.id ?? "anon"],
    enabled: Boolean(user?.id),
    queryFn: () => fn({}),
  });
}

function ContractorHome() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading, error } = useContractorWorkspace();
  const inviteFn = useServerFn(inviteContractorMember);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const partnerships: any[] = data?.partnerships ?? [];
  const memberships: any[] = data?.memberships ?? [];
  const sites: any[] = data?.sites ?? [];
  const shifts: any[] = data?.shifts ?? [];
  const postOrders: any[] = data?.postOrders ?? [];
  const teammates: any[] = data?.teammates ?? [];

  const isOwner = memberships.some((m) => m.contractor_role === "contractor_owner");
  const primary = partnerships[0];
  const mine = useMemo(
    () => shifts.filter((s) => s.officer_id === user?.id && s.status !== "cancelled"),
    [shifts, user?.id],
  );
  const unclaimed = shifts.filter((s) => !s.officer_id && s.status !== "cancelled");

  if (isLoading) return <p className="p-6 text-sm text-muted-foreground">Loading your contracted work…</p>;
  if (error) {
    return (
      <p className="m-4 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        Data source unavailable. Try again shortly.
      </p>
    );
  }
  if (partnerships.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-4 py-10">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <Briefcase className="mx-auto h-7 w-7 text-muted-foreground" aria-hidden />
          <h1 className="mt-3 font-display text-xl font-semibold">No contracted work</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You aren't part of a contractor partner relationship right now. When an organization invites your
            company, the invitation arrives by email.
          </p>
        </div>
      </div>
    );
  }

  const step = mine.length > 0 ? 7 : unclaimed.length > 0 ? 6 : sites.length > 0 ? 5 : teammates.length > 1 ? 4 : 3;

  const sendMemberInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res: any = await inviteFn({
        data: { partnershipId: primary.id, email: email.trim().toLowerCase(), origin: window.location.origin },
      });
      const link = `${window.location.origin}/accept-contract/${res.token}`;
      try { await navigator.clipboard.writeText(link); } catch { /* clipboard optional */ }
      if (res.emailSent) toast.success("Invitation emailed — link also copied");
      else toast.warning("Invitation created, but the email couldn't be sent. Link copied — share it directly.");
      setEmail(""); setOpen(false);
      await qc.invalidateQueries({ queryKey: ["contractor-workspace", user?.id ?? "anon"] });
    } catch (err: any) {
      toast.error(typeof err?.message === "string" ? err.message : "Could not send the invitation");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:pt-6">
      <header className="mb-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Contracted work</p>
        <h1 className="mt-1 font-display text-2xl font-semibold">{primary.contractor_name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Contracted to {primary.inviting_org_name ?? "the inviting organization"} ·{" "}
          {PARTNERSHIP_STATUS_LABEL[primary.status as PartnershipStatus] ?? primary.status}
        </p>
        {!partnershipOperational(primary) ? (
          <p className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-300">
            This relationship isn't active right now. Your records stay available, but new shifts and site
            access are paused.
          </p>
        ) : null}
      </header>

      <ContractorWorkflowStrip currentStep={step} className="mb-5" />

      <div className="mb-5 grid grid-cols-2 gap-2">
        <Link to="/contractor/shift-claim" className="rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
          <ClipboardList className="h-5 w-5 text-primary" aria-hidden />
          <p className="mt-2 text-sm font-semibold">Shift Claim</p>
          <p className="text-xs text-muted-foreground">{unclaimed.length} unclaimed</p>
        </Link>
        <Link to="/contractor/schedule" className="rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
          <CalendarClock className="h-5 w-5 text-primary" aria-hidden />
          <p className="mt-2 text-sm font-semibold">Schedule</p>
          <p className="text-xs text-muted-foreground">{mine.length} of yours</p>
        </Link>
        <Link to="/contractor/pay-periods" search={{ id: undefined }} className="rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
          <FileText className="h-5 w-5 text-primary" aria-hidden />
          <p className="mt-2 text-sm font-semibold">Pay periods</p>
          <p className="text-xs text-muted-foreground">{(data?.payPeriods ?? []).length} recorded</p>
        </Link>
        <Link to="/time-clock" className="rounded-xl border border-border bg-card p-4 transition hover:border-primary/50">
          <MapPin className="h-5 w-5 text-primary" aria-hidden />
          <p className="mt-2 text-sm font-semibold">Time clock</p>
          <p className="text-xs text-muted-foreground">Clock in on site</p>
        </Link>
      </div>

      <section className="mb-5 rounded-xl border border-border bg-card p-4">
        <h2 className="font-display text-lg font-semibold">Your next shifts</h2>
        <ul className="mt-3 space-y-2">
          {mine.length === 0 ? (
            <li className="text-sm text-muted-foreground">
              Nothing claimed yet — open the Shift Claim board to pick up work.
            </li>
          ) : mine.slice(0, 5).map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">
                  {new Date(s.starts_at).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {sites.find((x) => x.id === s.site_id)?.name ?? "Assigned site"} · {shiftHours(s.starts_at, s.ends_at)} h
                </p>
              </div>
              <ShiftStatusBadge status={contractedShiftStatus(s)} />
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-5 rounded-xl border border-border bg-card p-4">
        <h2 className="font-display text-lg font-semibold">Assigned sites</h2>
        <ul className="mt-3 space-y-2">
          {sites.length === 0 ? (
            <li className="text-sm text-muted-foreground">No sites assigned yet.</li>
          ) : sites.map((s) => {
            const orders = postOrders.filter((o) => o.site_id === s.id);
            return (
              <li key={s.id} className="rounded-lg border border-border p-3">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <MapPin className="h-3.5 w-3.5 text-primary" aria-hidden /> {s.name}
                </p>
                <p className="text-xs text-muted-foreground">{s.address ?? "—"}</p>
                {orders.length > 0 ? (
                  <details className="mt-2">
                    <summary className="min-h-9 cursor-pointer text-xs font-medium text-primary">
                      Post orders ({orders.length})
                    </summary>
                    <ul className="mt-2 space-y-2">
                      {orders.map((o) => (
                        <li key={o.id} className="rounded border border-border p-2">
                          <p className="text-xs font-semibold">{o.title}</p>
                          {o.body ? <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{o.body}</p> : null}
                        </li>
                      ))}
                    </ul>
                  </details>
                ) : (
                  <p className="mt-1 text-xs text-muted-foreground">No post orders shared for this site yet.</p>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="mb-5 rounded-xl border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold">
            <Users className="h-4 w-4" aria-hidden /> Your personnel
          </h2>
          {isOwner ? (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="min-h-10">
                  <Plus className="mr-1 h-4 w-4" aria-hidden /> Invite member
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Invite a contractor member</DialogTitle>
                  <DialogDescription>
                    They join your contractor organization for this assignment only. They never become
                    personnel of the inviting organization, and they don't use anyone's paid seats.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={sendMemberInvite} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cm-email">Email</Label>
                    <Input id="cm-email" type="email" required className="min-h-11" value={email}
                      onChange={(e) => setEmail(e.target.value)} placeholder="guard@abcsecurity.com" />
                  </div>
                  <ContractorDisclaimer />
                  <DialogFooter>
                    <Button type="submit" className="min-h-12 w-full" disabled={busy}>
                      {busy ? "Sending…" : "Send invitation"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : null}
        </div>
        <ul className="mt-3 space-y-2">
          {teammates.map((t) => (
            <li key={t.id} className="flex items-center justify-between gap-2 rounded-lg border border-border p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">
                  {t.profile?.full_name ?? t.profile?.email ?? "Contractor member"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t.contractor_role === "contractor_owner" ? "Contractor Organization Owner" : "Contractor member"}
                  {t.active ? "" : " · inactive"}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <ContractorDisclaimer />
    </div>
  );
}
